// ─── 网络工具函数 ────────────────────────────────────────────────────────────────

// IP 格式化（将IP转换为各种格式）
export function formatIp(ip) {
  const parts = ip.trim().split('.');
  if (parts.length !== 4 || parts.some(p => isNaN(p) || +p < 0 || +p > 255)) {
    // Try IPv6
    return { error: '请输入有效的 IPv4 地址', ip };
  }
  const n = parts.reduce((acc, p) => (acc << 8) + parseInt(p), 0) >>> 0;
  return {
    dotted: ip,
    decimal: n,
    hex: '0x' + n.toString(16).toUpperCase().padStart(8, '0'),
    octal: '0' + n.toString(8),
    binary: parts.map(p => parseInt(p).toString(2).padStart(8, '0')).join('.'),
    cidr_class: +parts[0] < 128 ? 'A类' : +parts[0] < 192 ? 'B类' : +parts[0] < 224 ? 'C类' : 'D/E类',
    is_private: isPrivateIp(ip),
    is_loopback: ip.startsWith('127.'),
  };
}

function isPrivateIp(ip) {
  const [a, b] = ip.split('.').map(Number);
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

// HTTP 状态码查询
export const HTTP_STATUS_CODES = {
  // 1xx
  100: { text: 'Continue', desc: '继续发送请求体' },
  101: { text: 'Switching Protocols', desc: '协议切换（如 WebSocket）' },
  102: { text: 'Processing', desc: '处理中' },
  // 2xx
  200: { text: 'OK', desc: '请求成功' },
  201: { text: 'Created', desc: '资源已创建' },
  202: { text: 'Accepted', desc: '请求已接受，但尚未处理' },
  204: { text: 'No Content', desc: '成功但无响应体' },
  206: { text: 'Partial Content', desc: '部分内容（断点续传）' },
  // 3xx
  301: { text: 'Moved Permanently', desc: '永久重定向' },
  302: { text: 'Found', desc: '临时重定向' },
  304: { text: 'Not Modified', desc: '资源未修改，使用缓存' },
  307: { text: 'Temporary Redirect', desc: '临时重定向（保持方法）' },
  308: { text: 'Permanent Redirect', desc: '永久重定向（保持方法）' },
  // 4xx
  400: { text: 'Bad Request', desc: '请求语法错误' },
  401: { text: 'Unauthorized', desc: '需要身份验证' },
  403: { text: 'Forbidden', desc: '服务器拒绝请求' },
  404: { text: 'Not Found', desc: '资源不存在' },
  405: { text: 'Method Not Allowed', desc: '请求方法不支持' },
  408: { text: 'Request Timeout', desc: '请求超时' },
  409: { text: 'Conflict', desc: '请求与服务器状态冲突' },
  410: { text: 'Gone', desc: '资源已永久删除' },
  413: { text: 'Payload Too Large', desc: '请求体过大' },
  414: { text: 'URI Too Long', desc: 'URI 太长' },
  415: { text: 'Unsupported Media Type', desc: '不支持的媒体类型' },
  422: { text: 'Unprocessable Entity', desc: '语义错误，无法处理' },
  429: { text: 'Too Many Requests', desc: '请求过于频繁（限流）' },
  // 5xx
  500: { text: 'Internal Server Error', desc: '服务器内部错误' },
  501: { text: 'Not Implemented', desc: '功能未实现' },
  502: { text: 'Bad Gateway', desc: '网关无效响应' },
  503: { text: 'Service Unavailable', desc: '服务不可用' },
  504: { text: 'Gateway Timeout', desc: '网关超时' },
  505: { text: 'HTTP Version Not Supported', desc: '不支持的 HTTP 版本' },
};

export function lookupHttpStatus(code) {
  const n = parseInt(code);
  const info = HTTP_STATUS_CODES[n];
  if (!info) throw new Error(`未知的状态码: ${code}`);
  const group = n < 200 ? '1xx 信息' : n < 300 ? '2xx 成功' : n < 400 ? '3xx 重定向' : n < 500 ? '4xx 客户端错误' : '5xx 服务端错误';
  return { code: n, ...info, group };
}

// 常用端口
export const COMMON_PORTS = [
  { port: 20, proto: 'TCP', service: 'FTP Data', desc: 'FTP 数据传输' },
  { port: 21, proto: 'TCP', service: 'FTP Control', desc: 'FTP 控制' },
  { port: 22, proto: 'TCP', service: 'SSH', desc: '安全外壳协议' },
  { port: 23, proto: 'TCP', service: 'Telnet', desc: '远程登录（不加密）' },
  { port: 25, proto: 'TCP', service: 'SMTP', desc: '邮件发送' },
  { port: 53, proto: 'TCP/UDP', service: 'DNS', desc: '域名解析' },
  { port: 67, proto: 'UDP', service: 'DHCP Server', desc: 'DHCP 服务' },
  { port: 68, proto: 'UDP', service: 'DHCP Client', desc: 'DHCP 客户端' },
  { port: 80, proto: 'TCP', service: 'HTTP', desc: '超文本传输协议' },
  { port: 110, proto: 'TCP', service: 'POP3', desc: '邮件接收' },
  { port: 143, proto: 'TCP', service: 'IMAP', desc: '邮件协议' },
  { port: 194, proto: 'TCP', service: 'IRC', desc: '互联网中继聊天' },
  { port: 443, proto: 'TCP', service: 'HTTPS', desc: 'HTTP 加密版' },
  { port: 465, proto: 'TCP', service: 'SMTPS', desc: 'SMTP 加密' },
  { port: 587, proto: 'TCP', service: 'SMTP Alt', desc: 'SMTP 提交' },
  { port: 993, proto: 'TCP', service: 'IMAPS', desc: 'IMAP 加密' },
  { port: 995, proto: 'TCP', service: 'POP3S', desc: 'POP3 加密' },
  { port: 1080, proto: 'TCP', service: 'SOCKS', desc: 'SOCKS 代理' },
  { port: 1194, proto: 'TCP/UDP', service: 'OpenVPN', desc: 'OpenVPN' },
  { port: 1433, proto: 'TCP', service: 'MSSQL', desc: 'Microsoft SQL Server' },
  { port: 1521, proto: 'TCP', service: 'Oracle DB', desc: 'Oracle 数据库' },
  { port: 2181, proto: 'TCP', service: 'ZooKeeper', desc: 'ZooKeeper 服务' },
  { port: 2375, proto: 'TCP', service: 'Docker', desc: 'Docker API (不加密)' },
  { port: 2376, proto: 'TCP', service: 'Docker TLS', desc: 'Docker API (TLS)' },
  { port: 3000, proto: 'TCP', service: 'Dev Server', desc: '常用开发服务器端口' },
  { port: 3306, proto: 'TCP', service: 'MySQL', desc: 'MySQL 数据库' },
  { port: 3389, proto: 'TCP', service: 'RDP', desc: 'Windows 远程桌面' },
  { port: 4200, proto: 'TCP', service: 'Angular', desc: 'Angular 开发服务器' },
  { port: 5000, proto: 'TCP', service: 'Flask', desc: 'Flask 开发服务器' },
  { port: 5432, proto: 'TCP', service: 'PostgreSQL', desc: 'PostgreSQL 数据库' },
  { port: 5672, proto: 'TCP', service: 'RabbitMQ', desc: 'AMQP 消息队列' },
  { port: 5900, proto: 'TCP', service: 'VNC', desc: '虚拟网络计算' },
  { port: 6379, proto: 'TCP', service: 'Redis', desc: 'Redis 缓存数据库' },
  { port: 7077, proto: 'TCP', service: 'Spark', desc: 'Apache Spark' },
  { port: 8080, proto: 'TCP', service: 'HTTP Alt', desc: 'HTTP 替代端口' },
  { port: 8443, proto: 'TCP', service: 'HTTPS Alt', desc: 'HTTPS 替代端口' },
  { port: 9000, proto: 'TCP', service: 'SonarQube', desc: 'SonarQube / PHP-FPM' },
  { port: 9092, proto: 'TCP', service: 'Kafka', desc: 'Apache Kafka' },
  { port: 9200, proto: 'TCP', service: 'Elasticsearch', desc: 'Elasticsearch HTTP API' },
  { port: 9300, proto: 'TCP', service: 'Elasticsearch', desc: 'Elasticsearch 节点通信' },
  { port: 27017, proto: 'TCP', service: 'MongoDB', desc: 'MongoDB 数据库' },
  { port: 50070, proto: 'TCP', service: 'Hadoop', desc: 'HDFS NameNode' },
];

// User-Agent 解析
export function parseUserAgent(ua) {
  const result = { raw: ua, browser: 'Unknown', os: 'Unknown', device: 'Desktop', engine: 'Unknown', version: '' };

  // OS
  if (/Windows NT 10/.test(ua)) result.os = 'Windows 10/11';
  else if (/Windows NT 6\.3/.test(ua)) result.os = 'Windows 8.1';
  else if (/Windows NT 6\.1/.test(ua)) result.os = 'Windows 7';
  else if (/Mac OS X ([\d_]+)/.test(ua)) result.os = 'macOS ' + ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.');
  else if (/Android ([\d.]+)/.test(ua)) result.os = 'Android ' + ua.match(/Android ([\d.]+)/)?.[1];
  else if (/iPhone OS ([\d_]+)/.test(ua)) result.os = 'iOS ' + ua.match(/iPhone OS ([\d_]+)/)?.[1]?.replace(/_/g, '.');
  else if (/Linux/.test(ua)) result.os = 'Linux';

  // Device
  if (/iPhone|iPad|iPod/.test(ua)) result.device = /iPad/.test(ua) ? 'Tablet' : 'Mobile';
  else if (/Android/.test(ua) && /Mobile/.test(ua)) result.device = 'Mobile';
  else if (/Android/.test(ua)) result.device = 'Tablet';

  // Browser
  if (/Edg\/([.\d]+)/.test(ua)) { result.browser = 'Edge'; result.version = ua.match(/Edg\/([\d.]+)/)?.[1] ?? ''; }
  else if (/OPR\/([.\d]+)/.test(ua)) { result.browser = 'Opera'; result.version = ua.match(/OPR\/([\d.]+)/)?.[1] ?? ''; }
  else if (/Chrome\/([\d.]+)/.test(ua)) { result.browser = 'Chrome'; result.version = ua.match(/Chrome\/([\d.]+)/)?.[1] ?? ''; }
  else if (/Firefox\/([\d.]+)/.test(ua)) { result.browser = 'Firefox'; result.version = ua.match(/Firefox\/([\d.]+)/)?.[1] ?? ''; }
  else if (/Safari\/([\d.]+)/.test(ua) && /Version\/([\d.]+)/.test(ua)) { result.browser = 'Safari'; result.version = ua.match(/Version\/([\d.]+)/)?.[1] ?? ''; }

  // Engine
  if (/AppleWebKit/.test(ua)) result.engine = 'WebKit';
  else if (/Gecko/.test(ua)) result.engine = 'Gecko';
  else if (/Trident/.test(ua)) result.engine = 'Trident (IE)';

  return result;
}

// Hosts 文件编辑器辅助
export function parseHosts(text) {
  const entries = [];
  const lines = text.split('\n');
  for (const line of lines) {
    const t = line.trim();
    if (!t || t.startsWith('#')) { entries.push({ type: 'comment', raw: line }); continue; }
    const parts = t.split(/\s+/);
    if (parts.length >= 2) {
      entries.push({ type: 'entry', ip: parts[0], hosts: parts.slice(1), raw: line });
    } else {
      entries.push({ type: 'unknown', raw: line });
    }
  }
  return entries;
}

export function formatHosts(entries) {
  return entries.map(e => {
    if (e.type === 'entry') return `${e.ip.padEnd(20)} ${e.hosts.join(' ')}`;
    return e.raw;
  }).join('\n');
}
